import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, AlertTriangle } from "lucide-react";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { authApi, formatError, tokenStore } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { PasswordInput } from "../components/auth/PasswordInput";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const { refresh } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // No token in URL → render a friendly error instead of a broken form.
  if (!token) {
    return (
      <div className="min-h-screen flex flex-col px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="reset-missing-token"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#E07A5F]" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#9C3E1D]">
              Invalid reset link
            </span>
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-[#2D3142] tracking-tight">
            This link is missing a token.
          </h1>
          <p className="text-[#4F5D75] mt-3 max-w-sm text-sm leading-relaxed">
            Reset links look like <code>/reset-password?token=...</code>. Please open
            the link from your email — or request a new one.
          </p>
          <div className="mt-6 space-y-3">
            <Button
              onClick={() => navigate("/forgot-password")}
              className="w-full bg-[#277A82] hover:bg-[#1F5566] text-white h-12"
              data-testid="reset-request-new-btn"
            >
              Request a new link
            </Button>
            <Link
              to="/auth"
              className="block w-full text-center text-sm text-[#4F5D75] hover:text-[#2D3142]"
            >
              Back to sign in
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const r = await authApi.resetPassword(token, password);
      // Auto-login: stash the JWT, then have AuthContext re-fetch the user.
      tokenStore.set(r.token);
      try { localStorage.removeItem("qfresh_filters"); } catch (e) { void e; }
      const user = await refresh();
      toast.success("Password updated — you're signed in.");
      if (user && !user.household_id) navigate("/onboarding");
      else navigate("/");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-10">
      <button
        type="button"
        onClick={() => navigate("/auth")}
        data-testid="reset-back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-6 self-start"
      >
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </button>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <div className="mb-2">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#277A82]">
            QFresh
          </span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold brand-gradient-text leading-tight tracking-tight">
          Set a new password
        </h1>
        <p className="text-[#4F5D75] mt-3 max-w-sm">
          Pick something at least 6 characters. We'll sign you straight in once it's saved.
        </p>
      </motion.div>

      <motion.form
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        onSubmit={submit}
        className="mt-8 space-y-4"
        data-testid="reset-password-form"
      >
        <div>
          <Label className="text-[#2D3142] font-semibold flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-[#277A82]" /> New password
          </Label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            autoComplete="new-password"
            data-testid="reset-password-input"
          />
        </div>

        <div>
          <Label className="text-[#2D3142] font-semibold flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-[#277A82]" /> Confirm password
          </Label>
          <PasswordInput
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type it again"
            autoComplete="new-password"
            data-testid="reset-confirm-input"
          />
        </div>

        <Button
          type="submit"
          disabled={busy}
          data-testid="reset-submit-btn"
          className="w-full bg-[#277A82] hover:bg-[#1F5566] text-white h-12 mt-2"
        >
          {busy ? "Updating…" : "Save & sign in"}
        </Button>
      </motion.form>
    </div>
  );
};

export default ResetPassword;
